import {
  type ReactNode,
  useId,
  useMemo,
  useRef,
  Children,
  type ReactElement,
} from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { Flex } from '@dynatrace/strato-components/layouts';
import { ChevronDownSmallIcon } from '@dynatrace/strato-icons';

import { useFilterBarContext } from './hooks/useFilterBarContext.js';
import { Item } from './Item.js';
import { FilterBarActions } from './types/filter-state.js';
import { getTextFromNode } from './utils/getTextFromNode.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { SelectV2 } from '../../forms/selectv2/Select.js';
import { SelectV2MultiValue } from '../../forms/selectv2/types.js';
import { FormControlWithOverlayRef } from '../../forms/shared-types.js';

type MoreMenuProps = {
  children: ReactNode[];
};

/**
 * The moreMenu allows to hide and show further filters. All filterBar.Items inside are hidden initially.
 * @internal
 */
export const MoreMenu = ({ children }: MoreMenuProps) => {
  const intl = useIntl();
  const label = intl.formatMessage({
    defaultMessage: 'Add filter',
    id: '+BUznwQEJTiILiAa',
    description:
      'Message to display by default for adding more Filters to the FilterBar.',
  });

  const { focusClassName, focusProps } = useFocusRing({
    color: 'neutral',
    focusWithin: true,
  });

  const { dispatch, setLastAddedItem, state } = useFilterBarContext(),
    { itemsPinnedState } = state;

  const selectRef = useRef<FormControlWithOverlayRef | null>(null);

  const allPinnableFilters = useMemo<
    Record<string, { elem: ReactElement; label: string }>
  >(() => {
    const temp: Record<string, { elem: ReactElement; label: string }> = {};
    for (const child of Children.toArray(children)) {
      if (isSlot(child, Item)) {
        const uniqueName: string = child.props.name;
        temp[uniqueName] = {
          elem: child,
          label: getTextFromNode(child.props.label, intl, uniqueName),
        };
      }
    }
    return temp;
  }, [children, intl]);

  const optionalPinnedItems = useMemo(
    () =>
      Object.entries(itemsPinnedState || {}).reduce((keys, [key, value]) => {
        if (value === 'pinned-optional') {
          keys.push(key);
        }
        return keys;
      }, [] as Array<string | number>),
    [itemsPinnedState],
  );

  const handleSelectChange = (
    newValues: SelectV2MultiValue<string | number>,
  ) => {
    setLastAddedItem(null);

    const union = new Set([
      ...(newValues ?? []),
      ...(optionalPinnedItems || []),
    ]);

    // Check for newly selected items and pin those
    if (
      !optionalPinnedItems ||
      (newValues && optionalPinnedItems.length < newValues.length)
    ) {
      const filtersToPin = [...union].filter(
        (key) => !optionalPinnedItems?.includes(key),
      );

      selectRef?.current?.close();
      filtersToPin?.length && setLastAddedItem(filtersToPin[0]?.toString());

      dispatch({
        type: FilterBarActions.PIN,
        payload: {
          keys: filtersToPin,
        },
      });

      return;
    }

    // Check for newly unselected items and unpin those
    if (!newValues) {
      dispatch({
        type: FilterBarActions.UNPIN,
        payload: {
          keys: optionalPinnedItems ?? [],
        },
      });
    } else if (
      optionalPinnedItems &&
      optionalPinnedItems.length > newValues.length
    ) {
      const keys = [...union].filter((key) => !newValues?.includes(key));

      dispatch({
        type: FilterBarActions.UNPIN,
        payload: { keys },
      });
    }
  };

  const labelId = useId();

  return (
    <>
      {optionalPinnedItems.map(
        (key) =>
          allPinnableFilters[key] && (
            <Flex key={key} gap={2} alignItems="flex-end">
              {allPinnableFilters[key].elem}
            </Flex>
          ),
      )}
      <div style={{ maxWidth: '100%' }} {...focusProps}>
        <SelectV2
          value={optionalPinnedItems}
          onChange={handleSelectChange}
          id="user-select"
          multiple={true}
          clearable
          ref={selectRef}
        >
          <SelectV2.CustomTrigger>
            <Button as="div" id={labelId} className={focusClassName}>
              {label}
              <Button.Suffix>
                <ChevronDownSmallIcon />
              </Button.Suffix>
            </Button>
          </SelectV2.CustomTrigger>
          <SelectV2.Content width="fit-content">
            <SelectV2.Filter />
            {Object.keys(allPinnableFilters).map((key) => (
              <SelectV2.Option value={key} key={key}>
                {allPinnableFilters[key].label}
              </SelectV2.Option>
            ))}
          </SelectV2.Content>
        </SelectV2>
      </div>
    </>
  );
};

(MoreMenu as typeof MoreMenu & { displayName: string }).displayName =
  'FilterBar.MoreMenu';
